import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import type * as Y from 'yjs';
import type { Scene } from '../../../core/models/scene';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useDiagramAccess } from '../../../hooks/useDiagramAccess';
import type { DiagramSettings } from '../../../hooks/useDiagramSettings';
import { useDiagramStore } from '../../../stores/diagramStore';
import {
  orgPersonNodeTypeFromCardVariant,
  resolveOrgPersonCardVariant,
} from '../models/defaults';
import { resolveOrgCardAccent } from '../assistant/atlasManualDisplayTypes';
import { buildPeopleChartScene } from '../services/buildPeopleChartScene';
import { resolveAtlasSavedViewMatches } from '../services/AtlasSavedViewMatching';
import { useAtlasSavedView } from './useAtlasSavedView';
import { useOrgModel } from './useOrgModel';
import { useOrgPlanningScenarioProjection } from './useOrgPlanningScenarioProjection';
import { useDiagramPresentationScene } from '../../../hooks/useDiagramPresentationScene';
import { composeProjectedScene } from '../../../core/canvas/composeProjectedScene';
import { orgProjectionCompletenessError } from '../services/orgProjectionCompleteness';
import { subscribeProtectedPlanningInvalidation } from './protectedPlanningGeneration';
import { mayRetainOrgProjectionScene, mayShowCurrentOrgContext } from './orgProjectionContinuity';
import { temporalBoundaryRefetchDelay } from './useOrgModel';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
export {
  OrgProjectionIncompleteError as PeopleChartRecordCapError,
  orgProjectionCompletenessError as peopleChartRecordCapError,
} from '../services/orgProjectionCompleteness';

const readPresentationFromDoc = (doc: Y.Doc | null | undefined): {
  personNodeType: string;
  viewMode: string | undefined;
  cardAccent: string;
  nodeSpacing: number | undefined;
  verticalSpacing: number | undefined;
} => {
  if (!doc) {
    return {
      personNodeType: orgPersonNodeTypeFromCardVariant(resolveOrgPersonCardVariant(undefined)),
      viewMode: undefined,
      cardAccent: resolveOrgCardAccent(undefined),
      nodeSpacing: undefined,
      verticalSpacing: undefined,
    };
  }
  try {
    const raw = doc.getMap('diagramSettings').get('settings');
    if (typeof raw !== 'string' || !raw) {
      return {
        personNodeType: orgPersonNodeTypeFromCardVariant(resolveOrgPersonCardVariant(undefined)),
        viewMode: undefined,
        cardAccent: resolveOrgCardAccent(undefined),
        nodeSpacing: undefined,
        verticalSpacing: undefined,
      };
    }
    const settings = JSON.parse(raw) as DiagramSettings;
    const variant = resolveOrgPersonCardVariant(settings.orgchart?.personCardVariant);
    return {
      personNodeType: orgPersonNodeTypeFromCardVariant(variant),
      viewMode: settings.orgchart?.viewMode,
      cardAccent: resolveOrgCardAccent(settings.orgchart?.cardAccent),
      nodeSpacing: resolveFiniteSpacing(settings.orgchart?.nodeSpacing),
      verticalSpacing: resolveFiniteSpacing(settings.orgchart?.verticalSpacing),
    };
  } catch {
    return {
      personNodeType: orgPersonNodeTypeFromCardVariant(resolveOrgPersonCardVariant(undefined)),
      viewMode: undefined,
      cardAccent: resolveOrgCardAccent(undefined),
      nodeSpacing: undefined,
      verticalSpacing: undefined,
    };
  }
};

const resolveFiniteSpacing = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Hydrates canonical People/department/analytics diagrams from folder truth. */
export function usePeopleChartSync(
  diagramId: string | undefined,
  doc?: Y.Doc | null,
): {
  isPeopleChart: boolean;
  isLoading: boolean;
  hasProjectionData: boolean;
  isShowingLiveContext: boolean;
  /** The chart renders a server-filtered saved-view scene (or has not yet
   * confirmed source access) and must not issue folder reads. */
  isSceneScoped: boolean;
  error: unknown;
  folderId: string | null;
} {
  const { data: diagram } = useDiagramInfo(diagramId);
  const isPeopleChart = diagram?.viewDefinition?.projection === 'people_chart'
    || (diagram?.viewDefinition?.projection === 'planning_chart'
      && diagram.viewDefinition.filters?.presentationOnly === true
      && diagram.viewDefinition.filters.planningProjection !== 'position');
  const presentationScene = useDiagramPresentationScene(doc, isPeopleChart);
  // A `?dept=` lens narrows the live chart in place. Departments are slices of
  // the one live projection, not separate artifacts, so the filter rides the URL
  // and composes with whatever the diagram's own view definition already says.
  const search = useSearch({ strict: false }) as { dept?: string } | undefined;
  const deptLens = typeof search?.dept === 'string' && search.dept.trim() ? search.dept : undefined;
  const effectiveViewDefinition = useMemo(() => {
    const base = diagram?.viewDefinition;
    if (!deptLens) return base;
    return {
      ...(base ?? {}),
      filters: { ...(base?.filters ?? {}), departmentKey: deptLens },
    } as typeof base;
  }, [deptLens, diagram?.viewDefinition]);
  const folderId = isPeopleChart
    ? diagram?.viewDefinition?.sourceFolderId ?? diagram?.folderId ?? null
    : null;
  const savedViewId = diagram?.viewDefinition?.type === 'saved-people-view'
    ? diagram.viewDefinition.savedViewId ?? null
    : null;
  const authenticatedFetch = useAuthenticatedFetch();
  const savedViewAccess = useDiagramAccess(savedViewId ? diagramId : undefined);
  const isLinkSavedView = Boolean(savedViewId && savedViewAccess.data?.source === 'link');
  // Do not begin folder-scoped reads until we know whether this saved view is
  // authenticated access or an exact-diagram link. The latter must never make
  // a protected request, even during the access-query loading window.
  const isSavedViewAccessUnresolved = Boolean(savedViewId && !savedViewAccess.data);
  const savedView = useAtlasSavedView(folderId, savedViewId, {
    enabled: !isLinkSavedView && !isSavedViewAccessUnresolved,
  });
  const savedDefinition = savedView.data?.access === 'source' ? savedView.data.definition : null;
  // A viewer holding only the saved-view diagram's grant (an email share) has
  // no source-folder access either, so like a link guest it renders the
  // server-filtered scene. Folder reads wait until the source is confirmed.
  const sceneSource = isLinkSavedView ? 'link' : savedView.data?.access === 'scene' ? 'share' : null;
  const isSceneSavedView = sceneSource !== null;
  const isSceneScoped = Boolean(savedViewId) && !savedDefinition;
  const [presentationVersion, setPresentationVersion] = useState(0);
  // The server scene is built without the viewer's Y.Doc, so the saved view's
  // cosmetic settings ride the request. Changing them refetches the scene.
  const scenePresentationQuery = useMemo(() => {
    void presentationVersion;
    const presentation = readPresentationFromDoc(doc);
    const params = new URLSearchParams({
      personNodeType: presentation.personNodeType,
      cardAccent: presentation.cardAccent,
    });
    if (presentation.viewMode) params.set('viewMode', presentation.viewMode);
    if (presentation.nodeSpacing !== undefined) params.set('nodeSpacing', String(presentation.nodeSpacing));
    if (presentation.verticalSpacing !== undefined) params.set('verticalSpacing', String(presentation.verticalSpacing));
    return params.toString();
  }, [doc, presentationVersion]);
  const savedViewScene = useQuery<{ scene: Scene; graphVersion: number; revision: number; nextTemporalBoundary?: string | null }>({
    queryKey: ['atlas', 'org-model', folderId, 'saved-view-scene', sceneSource, diagramId, { presentation: scenePresentationQuery }],
    enabled: Boolean(sceneSource && diagramId && folderId),
    // Keep the current scene on screen while a presentation change refetches,
    // but never carry a scene across to a different view or access path.
    placeholderData: (previous, previousQuery) => previousQuery?.queryKey[4] === sceneSource
      && previousQuery.queryKey[5] === diagramId ? previous : undefined,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: 'always',
    refetchInterval: (query) => temporalBoundaryRefetchDelay(query.state.data?.nextTemporalBoundary),
    queryFn: async ({ signal }): Promise<{ scene: Scene; graphVersion: number; revision: number; nextTemporalBoundary?: string | null }> => {
      const path = sceneSource === 'link'
        ? `/api/link/diagrams/${encodeURIComponent(diagramId!)}/atlas/saved-view-scene`
        : `/api/folders/${encodeURIComponent(folderId!)}/atlas/saved-views/${encodeURIComponent(savedViewId!)}/scene`;
      const url = `${path}?${scenePresentationQuery}`;
      const response = await authenticatedFetch(url, { credentials: 'include', signal });
      if (!response.ok) throw new Error(`Saved Atlas view is unavailable (${response.status})`);
      return response.json();
    },
  });
  const orgModel = useOrgModel({ folderId, diagramId, enabled: isPeopleChart && !isSceneScoped });
  // When a draft scenario is selected for this diagram, render its permissioned
  // post-state instead of live truth. The projection is disposable query state
  // — never copied into the Y.Doc. Saved views never carry scenarios.
  const scenarioProjection = useOrgPlanningScenarioProjection({
    folderId,
    diagramId,
    projectionKind: 'people',
    enabled: Boolean(isPeopleChart && !savedViewId),
  });
  const isShowingLiveContext = mayShowCurrentOrgContext({
    selected: scenarioProjection.hasSelectedScenario,
    scenarioError: scenarioProjection.error,
    discoveryError: scenarioProjection.discoveryError,
    currentError: orgModel.error,
    currentModel: orgModel.data,
  });
  const effectiveModel = scenarioProjection.hasSelectedScenario && !isShowingLiveContext
    ? scenarioProjection.data?.model : orgModel.data;
  const recordCapError = useMemo(
    () => orgProjectionCompletenessError(effectiveModel),
    [effectiveModel],
  );
  const planningChart = diagram?.viewDefinition?.projection === 'planning_chart';
  // Memoized: an unconditional `new Error(...)` here would mint a fresh
  // object every render whenever the guard is true (including the brief
  // window before `folderId` resolves and the query is merely disabled,
  // not actually loading). That reference feeds the main scene-loading
  // effect's dependency array below, so an unmemoized one retriggers
  // `loadScene` every render — an infinite render loop, not just a bug.
  const savedViewError = useMemo(
    () => (!isLinkSavedView && !isSavedViewAccessUnresolved && savedViewId
      && (savedView.error || (!savedView.isLoading && !savedView.data))
      ? new Error('Saved Atlas view is unavailable')
      : null),
    [isLinkSavedView, isSavedViewAccessUnresolved, savedViewId, savedView.error, savedView.isLoading, savedView.data],
  );
  // Memoized for the same reason as savedViewError above:
  // orgProjectionCompletenessError(orgModel.data) also mints a fresh Error
  // instance on every call when the model is incomplete, and this reference
  // feeds the same effect dependency array.
  const projectionError = useMemo(
    () => {
      // A saved-view scene is the entire projection this viewer may see. A
      // stale failure from a protected folder query must not put an otherwise
      // rendered view into an error state.
      if (isSceneSavedView) return savedViewScene.error ?? null;
      return savedViewError ?? orgModel.error
        ?? orgProjectionCompletenessError(orgModel.data)
        ?? (scenarioProjection.hasSelectedScenario || planningChart ? scenarioProjection.discoveryError : null)
        ?? recordCapError ?? scenarioProjection.error;
    },
    [
      savedViewScene.error,
      isSceneSavedView,
      savedViewError,
      orgModel.error,
      orgModel.data,
      scenarioProjection.hasSelectedScenario,
      planningChart,
      scenarioProjection.discoveryError,
      recordCapError,
      scenarioProjection.error,
    ],
  );
  const renderableModel = isSceneScoped || (projectionError && !isShowingLiveContext) || (planningChart && scenarioProjection.isDiscovering)
    ? undefined
    : effectiveModel;
  const savedMatchingPersonIds = useMemo(() => {
    if (!renderableModel || !savedDefinition) return undefined;
    return resolveAtlasSavedViewMatches(renderableModel, savedDefinition);
  }, [renderableModel, savedDefinition]);
  const projectionViewDefinition = useMemo(() => savedMatchingPersonIds
    ? { ...(effectiveViewDefinition ?? {}), matchingPersonIds: savedMatchingPersonIds }
    : effectiveViewDefinition, [effectiveViewDefinition, savedMatchingPersonIds]);
  const loadScene = useDiagramStore((state) => state.loadScene);
  const setInitialLoading = useDiagramStore((state) => state.setInitialLoading);
  const previousScene = useRef<Scene | undefined>(undefined);
  const previousFolderId = useRef<string | null>(null);
  const previousDeptLens = useRef<string | undefined>(undefined);
  const retainedSceneAvailable = mayRetainOrgProjectionScene({
    hasScene: Boolean(previousScene.current),
    sameScope: previousFolderId.current === folderId && previousDeptLens.current === deptLens,
    error: projectionError,
  });

  useEffect(() => {
    if (!isPeopleChart) return;
    return subscribeProtectedPlanningInvalidation((invalidatedFolder) => {
      if (invalidatedFolder && invalidatedFolder !== folderId) return;
      previousScene.current = undefined;
      previousFolderId.current = null;
      loadScene(composeProjectedScene({ version: 1, nodes: [], edges: [] }, presentationScene));
    });
  }, [folderId, isPeopleChart, loadScene, presentationScene]);

  useEffect(() => {
    if (!isPeopleChart || !doc) return;
    const settingsMap = doc.getMap('diagramSettings');
    const handleSettingsChange = () => {
      setPresentationVersion((version) => version + 1);
    };
    settingsMap.observe(handleSettingsChange);
    return () => {
      settingsMap.unobserve(handleSettingsChange);
    };
  }, [doc, isPeopleChart]);

  useEffect(() => {
    if (!isPeopleChart) return;
    if (isSavedViewAccessUnresolved) return;
    if (isSceneSavedView) {
      const scene: Scene = !savedViewScene.error && savedViewScene.data
        ? savedViewScene.data.scene : { version: 1, nodes: [], edges: [] };
      loadScene(composeProjectedScene(scene, presentationScene));
      if (savedViewScene.data || savedViewScene.error) setInitialLoading(false);
      return;
    }
    if (!renderableModel || !folderId) {
      if (mayRetainOrgProjectionScene({
        hasScene: Boolean(previousScene.current),
        sameScope: previousFolderId.current === folderId && previousDeptLens.current === deptLens,
        error: projectionError,
      })) return;
      previousScene.current = undefined;
      previousFolderId.current = null;
      loadScene(composeProjectedScene({ version: 1, nodes: [], edges: [] }, presentationScene));
      return;
    }
    // Switching folder or department lens invalidates the coordinate-reuse
    // cache — a different slice is a different set of nodes, so carrying the
    // previous scene forward would pin them at stale positions.
    if (previousFolderId.current !== folderId || previousDeptLens.current !== deptLens) {
      previousScene.current = undefined;
      previousFolderId.current = folderId;
      previousDeptLens.current = deptLens;
    }
    const presentation = readPresentationFromDoc(doc);
    const scene = buildPeopleChartScene(
      renderableModel,
      projectionViewDefinition,
      previousScene.current,
      presentation,
    );
    previousScene.current = scene;
    loadScene(composeProjectedScene(scene, presentationScene), { preserveSelection: true });
    setInitialLoading(false);
  }, [
    deptLens,
    doc,
    isSceneSavedView,
    isSavedViewAccessUnresolved,
    savedViewScene.data,
    savedViewScene.error,
    renderableModel,
    projectionViewDefinition,
    folderId,
    isPeopleChart,
    loadScene,
    presentationVersion,
    presentationScene,
    projectionError,
    setInitialLoading,
  ]);

  useEffect(() => {
    if (isPeopleChart) return;
    previousScene.current = undefined;
    previousFolderId.current = null;
    previousDeptLens.current = undefined;
  }, [isPeopleChart]);

  return {
    isPeopleChart: Boolean(isPeopleChart),
    isLoading: Boolean(isPeopleChart && !projectionError && (isSavedViewAccessUnresolved || (isSceneSavedView ? !savedViewScene.data : !renderableModel && !retainedSceneAvailable))),
    hasProjectionData: Boolean(isSceneSavedView ? savedViewScene.data && !savedViewScene.error : !isSavedViewAccessUnresolved && (renderableModel || retainedSceneAvailable)),
    isShowingLiveContext,
    isSceneScoped,
    error: projectionError,
    folderId,
  };
}
